/**
 * Shared Shift Utilities
 * 
 * Reusable break scheduling and shift calculation utilities extracted from ShiftGenerator.
 * These functions are used by both on-demand fleet generator and fixed route shift allocator.
 */

import type { GeneratorConfig, BreakSchedule } from '../../components/map/components/auto-fleet-generator/types';
import { 
  parseTime, 
  isTimeInRange, 
  calculateTimeDifference, 
  addMinutes,
  roundToNearest15
} from '../../components/map/components/auto-fleet-generator/timeUtils';

/**
 * Configuration interface for shift utilities (subset of GeneratorConfig)
 */
export interface ShiftConfig {
  maxShiftTimeMinutes: number;
  breakLengthMinutes: number;
  minTimeBeforeBreakMinutes: number;
  minTimeAfterBreakMinutes: number;
  breakReductionDuringPeakMinutes: number;
  peakPeriods: Array<{ start: string; end: string }>;
}

/**
 * Convert GeneratorConfig to ShiftConfig
 */
export function extractShiftConfig(config: GeneratorConfig): ShiftConfig {
  return {
    maxShiftTimeMinutes: config.maxShiftTimeMinutes,
    breakLengthMinutes: config.breakLengthMinutes,
    minTimeBeforeBreakMinutes: config.minTimeBeforeBreakMinutes,
    minTimeAfterBreakMinutes: config.minTimeAfterBreakMinutes,
    breakReductionDuringPeakMinutes: config.breakReductionDuringPeakMinutes,
    peakPeriods: config.peakPeriods,
  };
}

/**
 * Check if a time (in minutes) falls within any peak period
 * 
 * @param timeMinutes - Time to check in minutes from midnight
 * @param peakPeriods - Array of peak periods with start/end times in HH:MM format
 * @returns True if time falls within any peak period
 */
export function isPeakPeriod(
  timeMinutes: number,
  peakPeriods: Array<{ start: string; end: string }>
): boolean {
  for (const peak of peakPeriods) {
    const peakStart = parseTime(peak.start);
    const peakEnd = parseTime(peak.end);
    if (isTimeInRange(timeMinutes, peakStart, peakEnd)) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate the number of breaks required for a shift
 * 
 * @param shiftDuration - Shift duration in minutes
 * @param shiftStart - Shift start time in minutes (since midnight)
 * @param config - Shift configuration
 * @returns Number of breaks needed
 */
export function calculateRequiredBreaks(
  shiftDuration: number,
  shiftStart: number,
  config: ShiftConfig
): number {
  // If shift is too short for breaks
  if (shiftDuration <= config.minTimeBeforeBreakMinutes + config.minTimeAfterBreakMinutes) {
    return 0;
  }

  // Determine break length (may be reduced during peak)
  let breakLen = config.breakLengthMinutes;
  if (isPeakPeriod(shiftStart, config.peakPeriods) && config.breakReductionDuringPeakMinutes > 0) {
    breakLen = Math.max(0, config.breakLengthMinutes - config.breakReductionDuringPeakMinutes);
  }

  if (breakLen === 0) {
    return 0;
  }

  // Calculate available time for breaks
  // We need to reserve:
  // - minTimeBeforeBreak at the start (for the first break)
  // - minTimeAfterBreak at the end (for the last break)
  // - The rest can be used for breaks
  const effectiveDuration = shiftDuration - (config.minTimeBeforeBreakMinutes + config.minTimeAfterBreakMinutes);
  if (effectiveDuration <= 0) {
    return 0;
  }

  // For a break to fit, we need:
  // - Already have minTimeBeforeBreak reserved at start
  // - breakLen minutes for the break itself
  // - Already have minTimeAfterBreak reserved at end (for the last break)
  // So the minimum service time needed is: minTimeBeforeBreak + breakLen + minTimeAfterBreak
  // But we've already reserved minTimeBeforeBreak and minTimeAfterBreak, so we just need breakLen
  // Actually, wait - we need to think about this differently.
  
  // Minimum total service time needed for ONE break:
  // - minTimeBeforeBreak (before break)
  // - breakLen (break duration)
  // - minTimeAfterBreak (after break)
  // Total: minTimeBeforeBreak + breakLen + minTimeAfterBreak
  
  // If effectiveDuration (after reserving min times) is >= breakLen, we can fit at least one break
  // But we also need to ensure there's enough space for the break itself
  const minServiceTimeForOneBreak = config.minTimeBeforeBreakMinutes + breakLen + config.minTimeAfterBreakMinutes;
  
  if (shiftDuration < minServiceTimeForOneBreak) {
    return 0;
  }
  
  // At least one break fits
  let breaksNeeded = 1;
  
  // For additional breaks, each one needs:
  // - Time for the break itself (breakLen)
  // - But we've already accounted for minTimeBeforeBreak and minTimeAfterBreak once
  // Additional breaks need space between them:
  //   After the first break, we need minTimeAfterBreak + breakLen + minTimeBeforeBreak for the next break
  //   Actually, between breaks, we need: breakLen + minTimeBeforeBreak + minTimeAfterBreak = 270 minutes
  
  // Calculate how much service time is left after the first break
  // First break takes: minTimeBeforeBreak + breakLen + minTimeAfterBreak = 270 minutes
  let remainingTime = shiftDuration - minServiceTimeForOneBreak;
  
  // Additional breaks each need spaceBetweenBreaks
  // Between breaks: we need breakLen (the break itself) + minTimeBeforeBreak + minTimeAfterBreak
  // But wait - actually, between consecutive breaks, we need:
  //   - End of first break to start of second break: minTimeAfterBreak + minTimeBeforeBreak (to meet requirements)
  //   - Plus breakLen for the second break itself
  // So: (minTimeAfterBreak + minTimeBeforeBreak) + breakLen = 240 + 30 = 270 minutes
  const spaceBetweenBreaks = config.minTimeAfterBreakMinutes + config.minTimeBeforeBreakMinutes + breakLen;
  
  // Each additional break needs spaceBetweenBreaks (because we need service time between breaks)
  while (remainingTime >= spaceBetweenBreaks) {
    breaksNeeded++;
    remainingTime -= spaceBetweenBreaks;
  }

  return breaksNeeded;
}

/**
 * Schedule breaks within a shift
 * Returns array of break schedules with start/end times in minutes
 * 
 * @param shiftStart - Shift start time in minutes from midnight
 * @param shiftEnd - Shift end time in minutes from midnight
 * @param numBreaks - Number of breaks to schedule
 * @param config - Shift configuration
 * @returns Array of break schedules
 */
export function scheduleBreaks(
  shiftStart: number,
  shiftEnd: number,
  numBreaks: number,
  config: ShiftConfig
): BreakSchedule[] {
  if (numBreaks === 0) {
    return [];
  }

  const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
  const availableTime = shiftDuration - (config.minTimeBeforeBreakMinutes + config.minTimeAfterBreakMinutes);

  // Determine break length (may be reduced during peak)
  let breakLen = config.breakLengthMinutes;
  if (isPeakPeriod(shiftStart, config.peakPeriods) && config.breakReductionDuringPeakMinutes > 0) {
    breakLen = Math.max(0, config.breakLengthMinutes - config.breakReductionDuringPeakMinutes);
  }

  if (breakLen === 0) {
    return [];
  }

  const breaks: BreakSchedule[] = [];

  if (numBreaks === 1) {
    // Single break: place at midpoint
    const breakStart = addMinutes(
      shiftStart,
      config.minTimeBeforeBreakMinutes + Math.floor(availableTime / 2)
    );
    breaks.push({
      startMinutes: breakStart,
      endMinutes: addMinutes(breakStart, breakLen),
      durationMinutes: breakLen,
    });
  } else {
    // Multiple breaks: distribute evenly
    const interval = (availableTime - (numBreaks * breakLen)) / (numBreaks + 1);

    for (let i = 0; i < numBreaks; i++) {
      const breakStart = addMinutes(
        shiftStart,
        config.minTimeBeforeBreakMinutes + Math.floor((i + 1) * interval) + i * breakLen
      );
      breaks.push({
        startMinutes: breakStart,
        endMinutes: addMinutes(breakStart, breakLen),
        durationMinutes: breakLen,
      });
    }
  }

  return breaks;
}

/**
 * Find staggered break times to avoid all vehicles breaking simultaneously
 * 
 * @param shiftStart - Shift start time in minutes from midnight
 * @param shiftDuration - Shift duration in minutes
 * @param vehicleIndex - Index of the vehicle (0-based) for staggering
 * @param config - Shift configuration
 * @returns Array of break schedules, or empty array if no breaks needed
 */
export function findStaggeredBreakTimes(
  shiftStart: number,
  shiftDuration: number,
  vehicleIndex: number,
  config: ShiftConfig
): BreakSchedule[] {
  const shiftEnd = addMinutes(shiftStart, shiftDuration);
  
  // Calculate how many breaks are needed
  const numBreaksNeeded = calculateRequiredBreaks(shiftDuration, shiftStart, config);
  
  if (numBreaksNeeded === 0) {
    return [];
  }

  // Calculate the optimal break window (middle portion of shift)
  const availableBreakWindow = shiftDuration - config.minTimeBeforeBreakMinutes - config.minTimeAfterBreakMinutes;
  
  if (availableBreakWindow < config.breakLengthMinutes) {
    // Not enough time for a break, return empty
    return [];
  }

  // Find potential break windows with finer granularity (every 30 minutes)
  const breakOptions: BreakSchedule[] = [];
  let currentTime = addMinutes(shiftStart, config.minTimeBeforeBreakMinutes);
  const breakWindowEnd = addMinutes(shiftEnd, -config.minTimeAfterBreakMinutes - config.breakLengthMinutes);
  
  while (currentTime <= breakWindowEnd) {
    const potentialBreakStart = currentTime;
    const potentialBreakEnd = addMinutes(potentialBreakStart, config.breakLengthMinutes);
    
    // Check if break doesn't overlap with peak periods (if peak periods are defined)
    const notInPeak = config.peakPeriods.length === 0 || 
                      (!isPeakPeriod(potentialBreakStart, config.peakPeriods) && 
                       !isPeakPeriod(potentialBreakEnd, config.peakPeriods));
    
    if (notInPeak) {
      const roundedStart = roundToNearest15(potentialBreakStart);
      breakOptions.push({
        startMinutes: roundedStart,
        endMinutes: addMinutes(roundedStart, config.breakLengthMinutes),
        durationMinutes: config.breakLengthMinutes,
      });
    }

    // Move to next 30-minute interval for better staggering
    currentTime = addMinutes(currentTime, 30);
  }

  // If no non-peak options found, create options even during peaks
  if (breakOptions.length === 0) {
    currentTime = addMinutes(shiftStart, config.minTimeBeforeBreakMinutes);
    while (currentTime <= breakWindowEnd) {
      const roundedStart = roundToNearest15(currentTime);
      breakOptions.push({
        startMinutes: roundedStart,
        endMinutes: addMinutes(roundedStart, config.breakLengthMinutes),
        durationMinutes: config.breakLengthMinutes,
      });
      currentTime = addMinutes(currentTime, 30);
    }
  }

  // Stagger breaks by vehicle index
  if (breakOptions.length > 0) {
    const breaks: BreakSchedule[] = [];
    
    // For multiple breaks, distribute them across the shift
    if (numBreaksNeeded === 1) {
      const chosenIndex = vehicleIndex % breakOptions.length;
      breaks.push(breakOptions[chosenIndex]);
    } else {
      // For multiple breaks, space them out and stagger by vehicle
      const spacing = Math.floor(breakOptions.length / (numBreaksNeeded + 1));
      for (let i = 0; i < numBreaksNeeded; i++) {
        const baseIndex = (i + 1) * spacing;
        const offset = vehicleIndex * 2; // Stagger by 2 positions per vehicle
        const chosenIndex = (baseIndex + offset) % breakOptions.length;
        breaks.push(breakOptions[chosenIndex]);
      }
    }
    
    return breaks;
  }

  // Fallback to regular scheduling if no options
  return scheduleBreaks(shiftStart, shiftEnd, numBreaksNeeded, config);
}

